import { createFileRoute } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FoundersReserve } from "@/components/FoundersReserve";
import { getFoundersProgress, getFoundersWall } from "@/lib/growth.functions";
import { useReveal } from "@/hooks/use-reveal";

const wallQuery = queryOptions({
  queryKey: ["founders", "wall"],
  queryFn: () => getFoundersWall(),
});

const progressQuery = queryOptions({
  queryKey: ["founders", "progress"],
  queryFn: () => getFoundersProgress(),
});

export const Route = createFileRoute("/founders")({
  head: () => ({
    meta: [
      { title: "Founders Edition — The Founders App lifetime access" },
      {
        name: "description",
        content:
          "A one-time price, top-tier access for life, and your name inscribed on The Founders App founders wall. Available only while the founding round is open.",
      },
      { property: "og:title", content: "Founders Edition — The Founders App lifetime access" },
      {
        property: "og:description",
        content: "Pay once. Keep top-tier The Founders App forever. Your name stays on the wall.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://thefoundersapp.com/founders" },
      { property: "og:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:image", content: "https://thefoundersapp.com/og-cover.jpg" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://thefoundersapp.com/founders" }],
  }),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(wallQuery),
      context.queryClient.ensureQueryData(progressQuery),
    ]);
  },
  component: Founders,
});

function Founders() {
  useReveal();
  const { data: wall } = useSuspenseQuery(wallQuery);
  const { data: progress } = useSuspenseQuery(progressQuery);
  const pct = Math.min(100, Math.round((progress.claimed / progress.goal) * 100));

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="aurora" aria-hidden />
      <SiteHeader />

      <main id="main" className="relative mx-auto w-full max-w-6xl px-6 pt-16 pb-24 sm:px-10">
        <p className="reveal micro-label">Founders Edition</p>
        <h1 className="reveal etched mt-5 max-w-3xl text-[2.5rem] leading-[0.95] font-semibold sm:text-6xl [--d:80ms]">
          Pay once. Own it forever.
        </h1>
        <p className="reveal mt-6 max-w-xl text-[0.95rem] leading-relaxed text-muted-foreground [--d:160ms]">
          $1,497 one time. Top-tier Vault access for the life of the product, every future instrument
          included, and your name inscribed here. The Founders Edition is priced as a long-term
          commitment, not a discount — it pays for itself in roughly four years and removes every renewal
          decision after that. When the founding round opens, it'll stay open until we hit our goal —
          then this price is gone for good.
        </p>

        <div className="reveal mt-10 grid gap-6 lg:grid-cols-[1.2fr_1fr] [--d:240ms]">
          <div className="soft-panel p-7">
            <div className="flex items-baseline justify-between">
              <p className="micro-label">Founding round</p>
              <span className="text-[0.625rem] tracking-[0.2em] text-muted-foreground/50 tabular-nums">
                {progress.claimed} / {progress.goal}
              </span>
            </div>
            <div className="mt-6 h-1 w-full overflow-hidden rounded-sm bg-border">
              <div className="h-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              {progress.closed
                ? "The founding round is closed. Names below are permanent."
                : `${progress.remaining} spots to reach the founding goal once the round opens.`}
            </p>

            <ul className="mt-8 space-y-3 text-sm text-muted-foreground">
              {[
                "Everything in Vault, for life — no renewal, no price rise",
                "Unlimited businesses, timeline filters, full JSON export",
                "Every instrument we ship from here on",
                "Your name or handle inscribed on this wall",
                "Direct line to us while we build",
              ].map((f) => (
                <li key={f} className="flex gap-3">
                  <span className="mt-2 h-px w-4 shrink-0 bg-border" />
                  {f}
                </li>
              ))}
            </ul>
          </div>

          <FoundersReserve />
        </div>

        <section className="reveal mt-20 border-t border-border/60 pt-12 [--d:120ms]">
          <p className="micro-label">The wall</p>
          <h2 className="etched mt-4 text-3xl font-semibold sm:text-4xl">
            The people who backed it early.
          </h2>
          {wall.length === 0 ? (
            <p className="mt-6 max-w-lg text-sm text-muted-foreground">
              No names yet. The first line on this wall is still open.
            </p>
          ) : (
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {wall.map((f, i) => (
                <div key={`${f.display_name}-${i}`} className="soft-panel flex items-baseline gap-3 px-5 py-4">
                  <span className="text-[0.625rem] tracking-[0.2em] text-muted-foreground/50 tabular-nums">
                    {String(i + 1).padStart(3, "0")}
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{f.display_name}</p>
                    {f.handle && <p className="text-xs text-muted-foreground">{f.handle}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
